import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

const STEPS_BASE = [
  {
    key: "welcome", target: null,
    title: "Welcome to your team portal",
    body: "A quick 60-second tour of the screens you'll use every day. You can replay it anytime from the ? button at the top.",
  },
  {
    key: "dashboard", target: "sidebar-nav-dashboard",
    title: "Dashboard",
    body: "Your day at a glance — today's visits and installs, money still owed, and new enquiries waiting for a reply.",
  },
  {
    key: "customers", target: "sidebar-nav-customers",
    title: "Customers",
    body: "Every customer in one place. Open a record to see measurements, photos, quotes and notes, or tap New Customer to add one.",
  },
  {
    key: "orders", target: "sidebar-nav-orders",
    title: "Orders",
    body: "Track each job from deposit to fitting. Open an order to edit measurements, record payments, email the sheet to the fitter, or mark it installed.",
  },
  {
    key: "schedule", target: "sidebar-nav-visits-installs",
    title: "Visits & Installs",
    body: "Your calendar of home visits and fitting dates — everything booked in, in date order.",
  },
];

const STEP_ADMIN = {
  key: "office", target: "sidebar-nav-quotes",
  title: "Office tools",
  body: "Your login also unlocks Leads, Quotes, Payments, Invoices, Materials, Reports, Team and Settings in this section.",
};

const STEP_DONE = {
  key: "done", target: null,
  title: "That's everything",
  body: "You're ready to go. New bookings from the website appear under Customers automatically — happy fitting!",
};

export function PortalTour({ user, open, onClose }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  const steps = useMemo(() => {
    const s = [...STEPS_BASE];
    if (user && (user.role === "admin" || user.role === "office")) s.push(STEP_ADMIN);
    s.push(STEP_DONE);
    return s;
  }, [user]);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const t = steps[step].target;
      const el = t ? document.querySelector(`[data-testid="${t}"]`) : null;
      if (el) {
        el.scrollIntoView({ block: "nearest" });
        const r = el.getBoundingClientRect();
        setRect(r.width > 0 && r.height > 0 ? r : null);
      } else {
        setRect(null);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, step, steps]);

  if (!open) return null;

  const s = steps[step];
  const last = step === steps.length - 1;

  const cardStyle = rect
    ? {
        top: Math.min(Math.max(16, rect.top - 8), window.innerHeight - 260),
        left: Math.min(rect.right + 20, window.innerWidth - 380),
      }
    : { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Portal tour" data-testid="portal-tour">
      {rect ? (
        <div
          className="absolute rounded-xl ring-2 ring-white transition-all duration-300"
          style={{
            top: rect.top - 6, left: rect.left - 6,
            width: rect.width + 12, height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(7, 17, 34, 0.62)",
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-navy-950/60" />
      )}

      <div
        className="absolute w-[min(92vw,360px)] rounded-2xl bg-white p-6 shadow-2xl"
        style={cardStyle}
        data-testid="tour-card"
      >
        <button
          type="button"
          aria-label="Skip tour"
          data-testid="tour-skip"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-1.5 text-slate-400 hover:text-navy-900 hover:bg-warm-100 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400" data-testid="tour-step-counter">
          Step {step + 1} of {steps.length}
        </p>
        <h2 className="font-serif text-xl text-navy-900 mt-1.5" data-testid="tour-title">{s.title}</h2>
        <p className="text-sm text-slate-600 leading-relaxed mt-2" data-testid="tour-body">{s.body}</p>
        <div className="flex items-center gap-2 mt-5">
          {step > 0 && (
            <button
              type="button"
              data-testid="tour-back"
              onClick={() => setStep((v) => v - 1)}
              className="rounded-full border border-warm-300 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-navy-800/40 transition-colors"
            >
              Back
            </button>
          )}
          <button
            type="button"
            data-testid={last ? "tour-done" : "tour-next"}
            onClick={() => (last ? onClose() : setStep((v) => v + 1))}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-5 py-2 text-xs font-semibold text-white hover:bg-navy-800 transition-colors"
          >
            {last ? "Done — let's go" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}
